import React from 'react';
import {
  List, Datagrid, TextField, FunctionField, DateField, Filter, SearchInput, SelectInput,
  TopToolbar, useRecordContext, usePermissions,
} from 'react-admin';
import { Chip } from '@mui/material';
import CustomExportButton from '../../../components/CustomExportButton';
import ListPagination from '../../../components/ListPagination';
import { DATE_FORMATER, PERMISSIONS } from '../../../constants';
import { formatCpf, formatPartiallyHiddenCpf } from '../../../utils';
import {
  ENROLLMENT_STATUS_CHOICES, ENROLLMENT_STATUS_COLORS, ENROLLMENT_STATUS_LABELS,
  REWARD_STATUS_COLORS, REWARD_STATUS_LABELS, RewardStatus,
} from './labels';
import { __t } from '../../../i18n/t';
import ShowLink from './ShowLink';

const CustomerCpfCell: React.FC<{ label?: string; sortable?: boolean }> = () => {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const cpf = record?.customer?.cpf;
  if (!cpf) return <span>-</span>;
  const isAdmin = !!permissions?.includes(PERMISSIONS.ADMIN);
  const text = isAdmin ? formatCpf(cpf) : formatPartiallyHiddenCpf(cpf);
  return record?.customer?.id ? <ShowLink to={`/customers/${record.customer.id}/show`}>{text}</ShowLink> : <span>{text}</span>;
};

const CustomerNameCell: React.FC<{ label?: string; sortable?: boolean }> = () => {
  const record = useRecordContext();
  const customer = record?.customer;
  if (!customer?.name) return <span>-</span>;
  return customer.id ? <ShowLink to={`/customers/${customer.id}/show`}>{customer.name}</ShowLink> : <span>{customer.name}</span>;
};

const ReturnValueCell: React.FC<{ label?: string; sortBy?: string; sortable?: boolean }> = () => {
  const record = useRecordContext();
  if (record?.returnValue == null) return <span>—</span>;
  const text = `R$ ${Number(record.returnValue).toFixed(2)}`;
  return record.returnFillinId ? <ShowLink to={`/fillins/${record.returnFillinId}/show`}>{text}</ShowLink> : <span>{text}</span>;
};

const preExporter = async (enrollments: any[]) => enrollments.map((e) => ({
  cliente: e.customer?.name || '-',
  cpf: formatPartiallyHiddenCpf(e.customer?.cpf),
  celular: e.customer?.cellphone || '-',
  situacao: ENROLLMENT_STATUS_LABELS[e.status as keyof typeof ENROLLMENT_STATUS_LABELS] || e.status,
  nivelDaVolta: e.returnLevelName || '-',
  brinde: e.rewardStatus ? (REWARD_STATUS_LABELS[e.rewardStatus as RewardStatus] || e.rewardStatus) : '-',
  usouCupom: e.usedCoupon ? 'Sim' : 'Não',
  valorDoDesconto: e.discountValue != null ? Number(e.discountValue).toFixed(2) : '-',
  valorDaVolta: e.returnValue != null ? Number(e.returnValue).toFixed(2) : '-',
  inscritoEm: e.enrolledAt ? new Date(e.enrolledAt).toLocaleString('pt-BR') : '-',
  recuperadoEm: e.recoveredAt ? new Date(e.recoveredAt).toLocaleString('pt-BR') : '-',
}));

const CustomersActions: React.FC = () => (
  <TopToolbar>
    <CustomExportButton fileName="clientes-campanha" preExport={preExporter} isReport={false} maxResults={200000} />
  </TopToolbar>
);

interface CustomersFilterProps { levels: Array<{ id: string; name: string }>; }

const CustomersFilter: React.FC<any> = ({ levels, ...props }) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
    <SelectInput source="status" label="Situação" choices={ENROLLMENT_STATUS_CHOICES} alwaysOn />
    <SelectInput source="levelId" label="Nível da volta" choices={levels} alwaysOn />
  </Filter>
);

interface CustomersTabProps { campaign: any; }

const CustomersTab: React.FC<CustomersTabProps> = ({ campaign }) => {
  const levelChoices = (campaign.levels || [])
    .sort((a: any, b: any) => a.levelOrder - b.levelOrder)
    .map((l: any) => ({ id: l.id, name: l.name }));

  // Campanha só de desconto não tem o que mostrar na coluna: seria uma coluna de traços.
  const temBrinde = (campaign.levels || []).some((l: any) => l.reward || l.rewardCost != null);

  return (
    <List
      resource={`customer-recovery-rules/${campaign.id}/enrollments`}
      title=" "
      perPage={25}
      pagination={<ListPagination />}
      sort={{ field: 'enrolledAt', order: 'DESC' }}
      filters={<CustomersFilter levels={levelChoices} />}
      actions={<CustomersActions />}
      disableSyncWithLocation
    >
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <CustomerNameCell label="Cliente" sortable={false} />
        <CustomerCpfCell label="CPF" sortable={false} />
        <FunctionField
          label="Situação"
          sortable={false}
          render={(record: any) => (
            <Chip
              label={ENROLLMENT_STATUS_LABELS[record.status as keyof typeof ENROLLMENT_STATUS_LABELS] || record.status}
              color={ENROLLMENT_STATUS_COLORS[record.status as keyof typeof ENROLLMENT_STATUS_COLORS] as any}
              size="small"
            />
          )}
        />
        <TextField source="returnLevelName" label="Nível da volta" sortable={false} emptyText="—" />
        {temBrinde && (
          <FunctionField
            label="Brinde"
            sortable={false}
            render={(record: any) => {
              const status = record.rewardStatus as RewardStatus | null;
              if (!status) { return <span>—</span>; }
              return (
                <Chip
                  label={__t(REWARD_STATUS_LABELS[status] || status)}
                  color={REWARD_STATUS_COLORS[status] as any}
                  size="small"
                  variant="outlined"
                />
              );
            }}
          />
        )}
        <FunctionField label="Usou cupom" sortable={false} render={(record: any) => (record.usedCoupon ? 'Sim' : 'Não')} />
        <FunctionField
          // Em nível de brinde esta coluna carrega o CUSTO do brinde conquistado, não um
          // desconto em combustível: é o mesmo `discount_value`, e é ele que o teto soma.
          label={temBrinde ? __t('Valor') : __t('Desconto')}
          sortBy="discountValue"
          render={(record: any) => (record.discountValue != null ? `R$ ${Number(record.discountValue).toFixed(2)}` : '—')}
        />
        <ReturnValueCell label="Valor da volta" sortBy="returnValue" />
        <DateField source="enrolledAt" label="Inscrito em" options={DATE_FORMATER} showTime locales="pt-BR" emptyText="—" />
        <DateField source="recoveredAt" label="Recuperado em" options={DATE_FORMATER} showTime locales="pt-BR" emptyText="—" />
      </Datagrid>
    </List>
  );
};

export default CustomersTab;
